import { returnError400WithEmptyPayload, returnNoErrorWithPayload, StoragePermissionDTOUpdateType, StoragePermissionDTOUserOrGroup, } from './model/model';
import { getGroupIdsNullSafe_from_StorageV1CustomfieldPermissions, getUserIdsNullSafe_from_StorageV1CustomfieldPermissions, splitGroupIndexIntoTwoPieces_beforePersistingIt, } from '.';
import { computeFieldPermissionStorageKey, computeUserOrGroupStorageKey, doesUserOrGroupPermissionAlreadyExist, enforceValid_StoragePermissionDTOPatchGroupPermissions, enforceValid_StoragePermissionDTOPatchUserPermissions, removePermissionFromExistingAndDoNotFailOnNotExisting, } from './storage-service-helper';
import { MAX_BYTE_SIZE_STORAGE_LIMIT, MAX_BYTE_SIZE_STORAGE_LIMIT_GROUP_INDEX_OVERALL, MAX_PERSISTABLE_FIELDIDS_AND_CONTEXTIDS_PER_USER_OR_GROUP, MAX_PERSISTABLE_GROUPIDS_IN_GROUP_INDEX, } from './limits';
import { byteSizeOfJsonObject } from './util';
export class CustomStorageService {
    storage;
    startsWith;
    constructor(storage, startsWith) {
        this.storage = storage;
        this.startsWith = startsWith;
    }
    GROUP_PERMISSION_INDEX_V1_PART1 = 'gpermindexv1'; // NOTE: LEGACY KEY! DO NOT CHANGE!
    GROUP_PERMISSION_INDEX_V1_PART2 = 'gpermindexv1#part02';
    _enforceMaximumStorageSizeOfPermissions(existingPermissions, maxCount, maxBytes, tag) {
        if (existingPermissions.length > maxCount) {
            return returnError400WithEmptyPayload(`Limit of maximum ${maxCount} count of permissions reached. (${tag})`, `${tag}_8923489`);
        }
        const byteSize = byteSizeOfJsonObject(existingPermissions);
        if (byteSize > maxBytes) {
            return returnError400WithEmptyPayload(`Limit of maximum ${maxBytes} bytes of permissions reached. (${tag})`, `${tag}_3453434`);
        }
        return returnNoErrorWithPayload({});
    }
    _enforceMaximumStorageSizeOfGroupIndex(groupIndex, tag) {
        if (groupIndex.length > MAX_PERSISTABLE_GROUPIDS_IN_GROUP_INDEX) {
            return returnError400WithEmptyPayload(`Limit of maximum ${MAX_PERSISTABLE_GROUPIDS_IN_GROUP_INDEX} count of group index reached. (${tag})`, `${tag}_893947`);
        }
        const byteSize = byteSizeOfJsonObject(groupIndex);
        if (byteSize > MAX_BYTE_SIZE_STORAGE_LIMIT_GROUP_INDEX_OVERALL) {
            return returnError400WithEmptyPayload(`Limit of maximum ${MAX_BYTE_SIZE_STORAGE_LIMIT_GROUP_INDEX_OVERALL} bytes of group index reached. (${tag})`, `${tag}_347234`);
        }
        return returnNoErrorWithPayload({});
    }
    /*
     Gets global or context specific permission
    */
    async getFieldPermissions(fieldId, contextId) {
        const permissionsInStorageFormat = await this._getFieldPermissions(fieldId, contextId);
        return {
            userIds: getUserIdsNullSafe_from_StorageV1CustomfieldPermissions(permissionsInStorageFormat),
            groupIds: getGroupIdsNullSafe_from_StorageV1CustomfieldPermissions(permissionsInStorageFormat),
        };
    }
    async getUserAndGroupPermissions(userAccountId, groupIds) {
        const finalPermissions = [];
        //
        // STEP 1: Get User Permissions
        //
        const userPermissionStorageKey = computeUserOrGroupStorageKey(userAccountId, StoragePermissionDTOUserOrGroup.USER);
        const userPermissionResult = await this.storage.get(userPermissionStorageKey);
        if (userPermissionResult && Array.isArray(userPermissionResult)) {
            // console.log('userPermissionResult', userPermissionResult);
            finalPermissions.push(...userPermissionResult);
        }
        //
        // STEP 2.1 Get Group Permission Index
        //
        const groupsThatHavePermissions = await this._getGroupIndex();
        // console.log('groupsThatHavePermissions', JSON.stringify(groupsThatHavePermissions, null, 2));
        //
        // STEP 2.2: Get Group Permissions
        //
        if (groupIds) {
            for (const groupId of groupIds) {
                if (groupsThatHavePermissions && groupsThatHavePermissions.indexOf(groupId) !== -1) {
                    // console.log('includes group');
                    const groupPermissionStorageKey = computeUserOrGroupStorageKey(groupId, StoragePermissionDTOUserOrGroup.GROUP);
                    // console.log('groupPermissionStorageKey', groupPermissionStorageKey);
                    const groupPermissionResult = await this.storage.get(groupPermissionStorageKey);
                    //console.log('groupPermissionResult', groupPermissionResult);
                    if (groupPermissionResult && Array.isArray(groupPermissionResult)) {
                        // console.log('groupPermissionResult', groupPermissionResult);
                        finalPermissions.push(...groupPermissionResult);
                    }
                }
            }
        }
        return finalPermissions;
    }
    async patchUserPermissions(patchPermissionObj) {
        //
        // GUARDS
        //
        const enforceValidity = await enforceValid_StoragePermissionDTOPatchUserPermissions(patchPermissionObj);
        if (enforceValidity.errorMessage) {
            return enforceValidity;
        }
        //
        // BUSINESS LOGIC
        //
        const userAccountId = patchPermissionObj.userAccountId;
        const permission = patchPermissionObj.permission;
        const type = patchPermissionObj.type;
        const existingPermissions = await this._getUserPermissions(userAccountId);
        const newPermissions = [].concat(existingPermissions);
        const objDoesUserPermissionAlreadyExist = doesUserOrGroupPermissionAlreadyExist(existingPermissions, permission.fieldId, permission.contextId);
        if (type === StoragePermissionDTOUpdateType.ADD) {
            if (!objDoesUserPermissionAlreadyExist.doesAlreadyExist) {
                // ADD NEW IF NOT EXISTS
                newPermissions.push(permission);
            }
            else {
                // UPDATE/OVERWRITE EXISTING
                newPermissions[objDoesUserPermissionAlreadyExist.index] = permission;
            }
            const enforceSizeCheck = this._enforceMaximumStorageSizeOfPermissions(newPermissions, MAX_PERSISTABLE_FIELDIDS_AND_CONTEXTIDS_PER_USER_OR_GROUP, MAX_BYTE_SIZE_STORAGE_LIMIT, 'MAXUPERM');
            if (enforceSizeCheck.hasError) {
                return enforceSizeCheck;
            }
            await this._setUserPermissions(userAccountId, newPermissions);
            await this._patchFieldPermissionForUser(userAccountId, permission, type);
        }
        else if (type === StoragePermissionDTOUpdateType.DELETE) {
            if (objDoesUserPermissionAlreadyExist.doesAlreadyExist) {
                // only delete when already added
                const newPermissionsWithDeletion = removePermissionFromExistingAndDoNotFailOnNotExisting(existingPermissions, permission.fieldId, permission.contextId);
                await this._setUserPermissions(userAccountId, newPermissionsWithDeletion);
                await this._patchFieldPermissionForUser(userAccountId, permission, type);
            }
        }
        // always if no error above
        return returnNoErrorWithPayload(null);
    }
    async patchGroupPermissions(patchPermissionObj) {
        //
        // GUARDS
        //
        const enforceValidity = await enforceValid_StoragePermissionDTOPatchGroupPermissions(patchPermissionObj);
        if (enforceValidity.errorMessage) {
            return enforceValidity;
        }
        //
        // BUSINESS LOGIC
        //
        const groupId = patchPermissionObj.groupId;
        const permission = patchPermissionObj.permission;
        const type = patchPermissionObj.type;
        const existingPermissions = await this._getGroupPermissions(groupId);
        const newPermissions = [].concat(existingPermissions);
        const objDoesGroupPermissionAlreadyExist = doesUserOrGroupPermissionAlreadyExist(existingPermissions, permission.fieldId, permission.contextId);
        if (type === StoragePermissionDTOUpdateType.ADD) {
            if (!objDoesGroupPermissionAlreadyExist.doesAlreadyExist) {
                // ADD NEW IF NOT EXISTS
                newPermissions.push(permission);
            }
            else {
                // UPDATE/OVERWRITE EXISTING
                newPermissions[objDoesGroupPermissionAlreadyExist.index] = permission;
            }
            const enforceSizeCheck = this._enforceMaximumStorageSizeOfPermissions(newPermissions, MAX_PERSISTABLE_FIELDIDS_AND_CONTEXTIDS_PER_USER_OR_GROUP, MAX_BYTE_SIZE_STORAGE_LIMIT, 'MAXGPERM');
            if (enforceSizeCheck.hasError) {
                return enforceSizeCheck;
            }
            const groupIndex = await this._getGroupIndex();
            const enforceGroupIndexSizeCheck = this._enforceMaximumStorageSizeOfGroupIndex(groupIndex, 'MAXGINDEX');
            if (enforceGroupIndexSizeCheck.hasError) {
                return enforceGroupIndexSizeCheck;
            }
            await this._setGroupPermissions(groupId, newPermissions, StoragePermissionDTOUpdateType.ADD);
            await this._patchFieldPermissionForGroup(groupId, permission, type);
        }
        else if (type === StoragePermissionDTOUpdateType.DELETE) {
            if (objDoesGroupPermissionAlreadyExist.doesAlreadyExist) {
                const newPermissionsWithDeletion = removePermissionFromExistingAndDoNotFailOnNotExisting(existingPermissions, permission.fieldId, permission.contextId);
                await this._setGroupPermissions(groupId, newPermissionsWithDeletion, StoragePermissionDTOUpdateType.DELETE);
                await this._patchFieldPermissionForGroup(groupId, permission, type);
            }
        }
        // always if no error
        return returnNoErrorWithPayload(null);
    }
    /*
      await storage.set('upermv1#5b10a2844c20165700ede21g#',
       [ { fieldId: "customfield_123", contextId: null }, { fieldId: "customfield_1235", contextId: 789 } ]);
    */
    async _setUserPermissions(userAccountId, permissions) {
        const userPermissionStorageKey = computeUserOrGroupStorageKey(userAccountId, StoragePermissionDTOUserOrGroup.USER);
        await this.storage.set(userPermissionStorageKey, permissions);
        return null;
    }
    async _getUserPermissions(userAccountId) {
        const userPermissionStorageKey = computeUserOrGroupStorageKey(userAccountId, StoragePermissionDTOUserOrGroup.USER);
        const existingPermissions = await this.storage.get(userPermissionStorageKey);
        const existingPermissionsNullSafe = existingPermissions && Array.isArray(existingPermissions) ? existingPermissions.filter((p) => p !== null) : [];
        return existingPermissionsNullSafe;
    }
    /*
      await storage.set('gpermv1#GROUPID#',
       [ { fieldId: "customfield_123", contextId: null }, { fieldId: "customfield_1235", contextId: 789 } ]);
    */
    async _setGroupPermissions(groupId, permissions, actionForGroupIndex) {
        const groupPermissionStorageKey = computeUserOrGroupStorageKey(groupId, StoragePermissionDTOUserOrGroup.GROUP);
        await this.storage.set(groupPermissionStorageKey, permissions);
        await this._updateGroupIndex(groupId, actionForGroupIndex);
        return null;
    }
    async _getGroupPermissions(groupId) {
        const groupPermissionStorageKey = computeUserOrGroupStorageKey(groupId, StoragePermissionDTOUserOrGroup.GROUP);
        const existing = await this.storage.get(groupPermissionStorageKey);
        const existingPermissionsNullSafe = existing && Array.isArray(existing) ? existing.filter((p) => p !== null) : [];
        return existingPermissionsNullSafe;
    }
    /*
    User permission is fine:
    await storage.set('upermv1#5b10a2844c20165700ede21g#',
       [ { fieldId: "customfield_123", contextId: null }, { fieldId: "customfield_1235", contextId: 789 } ]);
  
    Group permission not. It can lead to many requests, when user has e.g. 50 groups.
    We need a group index "groups with permissions"
  
    storage.set('gpermindexv1', { value: [ "team-a", "team-b" ] });
      => can be re-built with query from gpermv1#* keys
      => holds groupnames who have permissions set.
  
    storage.set('gpermv1#team-a#',
       [ { fieldId: "customfield_123", contextId: null }, { fieldId: "customfield_1235", contextId: 789 } ]);
  
    Since rel 2.18.0 it is splitted in two KVS entries:
      GROUP_PERMISSION_INDEX_V1_PART1
      GROUP_PERMISSION_INDEX_V1_PART2
    */
    async _getGroupIndex() {
        const groupPermissionIndexPart1 = await this.storage.get(this.GROUP_PERMISSION_INDEX_V1_PART1);
        if (groupPermissionIndexPart1 && groupPermissionIndexPart1['values'] && groupPermissionIndexPart1['values'].length > 0) {
            return groupPermissionIndexPart1.values;
        }
        const groupPermissionIndexPart2 = await this.storage.get(this.GROUP_PERMISSION_INDEX_V1_PART2);
        if (groupPermissionIndexPart2 && groupPermissionIndexPart2['values'] && groupPermissionIndexPart2['values'].length > 0) {
            return groupPermissionIndexPart2.values;
        }
        return [];
    }
    async _persistGroupIndex(groupIds) {
        // ENFORCEMENT OF MAX BYTESIZE AND 10.000 MAX IS PERFORMED HIGHER UP!
        // SEE TEST IN SPEC - 5000 groupids should be below 230KB and that should be the limit per storage
        // WE SHOULD MAKE IT 10.000 WITH TWO INDEX STORAGE ENTRIES MEANING EACH 5.000
        const chunks = splitGroupIndexIntoTwoPieces_beforePersistingIt(groupIds);
        await this.storage.set(this.GROUP_PERMISSION_INDEX_V1_PART1, { values: chunks.part1 });
        return this.storage.set(this.GROUP_PERMISSION_INDEX_V1_PART2, { values: chunks.part2 });
    }
    async _updateGroupIndex(groupId, type) {
        const existingIndex = await this._getGroupIndex(); // nullsafe!
        if (type === StoragePermissionDTOUpdateType.DELETE) {
            // DELETE IF GROUP HAS NO OTHER PERMISSIONS
            const hasGroupOtherPermissions = (await this._getGroupPermissions(groupId)).length > 0;
            if (!hasGroupOtherPermissions) {
                const newIndex = existingIndex.filter((g) => g !== groupId);
                await this._persistGroupIndex(newIndex);
            }
        }
        if (type === StoragePermissionDTOUpdateType.ADD && !existingIndex.includes(groupId)) {
            // ADD IF NOT ALREADY EXISTS
            const newIndex = existingIndex.concat([groupId]);
            await this._persistGroupIndex(newIndex);
        }
    }
    async _patchFieldPermissionForGroup(groupId, permission, type) {
        const existingPermission = await this._getFieldPermissions(permission.fieldId, permission.contextId); // IS NULLSAFE
        if (type === StoragePermissionDTOUpdateType.ADD) {
            const foundIndex = existingPermission.groupIds.findIndex((g) => g === groupId);
            if (foundIndex === -1) {
                existingPermission.groupIds.push(groupId);
                await this._setFieldPermissions(permission.fieldId, permission.contextId, existingPermission);
            }
        }
        else if (type === StoragePermissionDTOUpdateType.DELETE) {
            const existingPermissionWithDeletion = { userIds: existingPermission.userIds, groupIds: existingPermission.groupIds };
            existingPermissionWithDeletion.groupIds = existingPermission.groupIds.filter((g) => g !== groupId);
            await this._setFieldPermissions(permission.fieldId, permission.contextId, existingPermissionWithDeletion);
        }
    }
    async _patchFieldPermissionForUser(userAccountId, permission, type) {
        const existingPermission = await this._getFieldPermissions(permission.fieldId, permission.contextId); // IS NULLSAFE
        if (type === StoragePermissionDTOUpdateType.ADD) {
            const foundIndex = existingPermission.userIds.findIndex((u) => u === userAccountId);
            if (foundIndex === -1) {
                existingPermission.userIds.push(userAccountId);
                await this._setFieldPermissions(permission.fieldId, permission.contextId, existingPermission);
            }
        }
        else if (type === StoragePermissionDTOUpdateType.DELETE) {
            const existingPermissionWithDeletion = { userIds: [], groupIds: existingPermission.groupIds };
            existingPermissionWithDeletion.userIds = existingPermission.userIds.filter((u) => u !== userAccountId);
            await this._setFieldPermissions(permission.fieldId, permission.contextId, existingPermissionWithDeletion);
        }
    }
    async _getFieldPermissions(fieldId, contextId) {
        const storageKey = computeFieldPermissionStorageKey(fieldId, contextId);
        const permission = await this.storage.get(storageKey);
        // NULLSAFE
        return permission && permission.userIds && permission.groupIds ? permission : { userIds: [], groupIds: [] };
    }
    /*
    Is not used directly but via setGroupPermissions and setUserPermissions
  
    await storage.set('fpermv1#10023#global#', { users: ['5b10a2844c20165700ede21g'], groups: ['team-a'] });
    await storage.set('fpermv1#10023#14000#', { users: [], groups: ['team-b'] });
    */
    async _setFieldPermissions(fieldId, contextId, permissions) {
        // console.log('_setFieldPermissions', fieldId + '--' + contextId + '--' + JSON.stringify(permissions, null, 2));
        const storageKey = computeFieldPermissionStorageKey(fieldId, contextId);
        await this.storage.set(storageKey, permissions);
        return null;
    }
}
